/**
 * The product's name, and every name it shipped under before.
 *
 * Everything that carries the name on a user's machine or in a browser (the home folder,
 * environment variables, cookies, request headers) is derived from here, so an install
 * from an earlier release keeps working after a rename: the old names are read when the
 * new ones are absent.
 *
 * The rename codemod (tools/orgus-cli/src/rename/) rewrites PRODUCT_NAME and PRODUCT_SLUG
 * with the rest of the tree and only adds the new name to the front of SHIPPED_SLUGS, so after a rename the
 * previous name turns into a legacy name here without anyone editing this file.
 * Browser-safe: no node imports; callers pass the file system in.
 */

export const PRODUCT_NAME = 'Orgus';
export const PRODUCT_SLUG = 'orgus';

/** Every slug this product has shipped under, newest first; the codemod puts a new name in front. Never remove one. */
export const SHIPPED_SLUGS: readonly string[] = ['orgus', 'airside', 'flightdeck']; // rename:shipped

/** The earlier names, newest first. They are read, never written. */
export const LEGACY_SLUGS: readonly string[] = SHIPPED_SLUGS.filter((s) => s !== PRODUCT_SLUG);

/** The current slug first, then the legacy ones. */
export const BRAND_SLUGS: readonly string[] = [PRODUCT_SLUG, ...LEGACY_SLUGS];

const envPrefix = (slug: string): string => `${slug.toUpperCase()}_`;

/** The environment variable names for one setting, current first: envNames('HOME') is ['ORGUS_HOME', ...legacy]. */
export function envNames(suffix: string): string[] {
  return BRAND_SLUGS.map((s) => envPrefix(s) + suffix);
}

/** The value of the first of envNames(suffix) that is set and not empty. */
export function envValue(env: Readonly<Record<string, string | undefined>>, suffix: string): string | undefined {
  for (const name of envNames(suffix)) {
    const v = env[name];
    if (v) return v;
  }
  return undefined;
}

/**
 * Copy every variable set under a legacy prefix to the current prefix, when the current one
 * is unset, so code that reads only the current names still sees an older setup.
 * A newer legacy name wins over an older one. Returns the names it set.
 */
export function adoptLegacyEnv(env: Record<string, string | undefined>): string[] {
  const set: string[] = [];
  const current = envPrefix(PRODUCT_SLUG);
  for (const slug of LEGACY_SLUGS) {
    const prefix = envPrefix(slug);
    for (const [name, value] of Object.entries(env)) {
      if (!name.startsWith(prefix) || !value || name.length === prefix.length) continue;
      const target = current + name.slice(prefix.length);
      if (env[target]) continue;
      env[target] = value;
      set.push(target);
    }
  }
  return set;
}

/** What resolveProductHome needs from the platform; node:fs and node:path in practice. */
export interface HomeFs {
  homedir: string;
  isDir(p: string): boolean;
  join(...parts: string[]): string;
  resolve(p: string): string;
}

/**
 * The product's home folder: <PRODUCT>_HOME (or a legacy variable), else ~/.<slug>, else the
 * folder of an earlier name when only that one exists, so machines, audit and secrets stay.
 */
export function resolveProductHome(env: Readonly<Record<string, string | undefined>>, fs: HomeFs): string {
  const set = envValue(env, 'HOME');
  if (set) return fs.resolve(set);
  const fresh = fs.join(fs.homedir, `.${PRODUCT_SLUG}`);
  if (fs.isDir(fresh)) return fresh;
  for (const slug of LEGACY_SLUGS) {
    const legacy = fs.join(fs.homedir, `.${slug}`);
    if (fs.isDir(legacy)) return legacy;
  }
  return fresh;
}

function legacySuffix(name: string, sep: string): string | undefined {
  const lower = name.toLowerCase();
  for (const slug of LEGACY_SLUGS) {
    if (lower === slug) return '';
    if (lower.startsWith(slug + sep)) return name.slice(slug.length);
  }
  return undefined;
}

/**
 * The current name of a request header sent under an earlier name, in lower case
 * ('x-flightdeck' becomes 'x-orgus', 'x-flightdeck-org' becomes 'x-orgus-org');
 * undefined when the header is not a legacy one.
 */
export function currentHeaderName(name: string): string | undefined {
  const lower = name.toLowerCase();
  if (!lower.startsWith('x-')) return undefined;
  const rest = legacySuffix(lower.slice(2), '-');
  return rest === undefined ? undefined : `x-${PRODUCT_SLUG}${rest}`;
}

/** The current name of a cookie set under an earlier name ('flightdeck_session_4600' becomes 'orgus_session_4600'). */
export function currentCookieName(name: string): string | undefined {
  const rest = legacySuffix(name, '_');
  return rest === undefined || rest === '' ? undefined : `${PRODUCT_SLUG}${rest}`;
}

/** The part of the Web Storage API migrateLegacyStorage needs (localStorage in a browser). */
export interface KeyValueStorage {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Move browser storage keys saved under an earlier name ('flightdeck.sw', 'orgus.mobileOffer')
 * to the current one ('<slug>.sw'), once: the value moves when the current key is unset, and the
 * old key is removed either way. Keys are `<slug>.<name>` or `<slug>-<name>`. Returns the keys
 * it moved. Never throws (private mode, a full quota): the choices then last for this tab only.
 */
export function migrateLegacyStorage(storage: KeyValueStorage | null | undefined): string[] {
  if (!storage) return [];
  const moved: string[] = [];
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k) keys.push(k);
    }
    for (const key of keys) {
      const rest = legacySuffix(key, '.') ?? legacySuffix(key, '-');
      if (!rest) continue;
      const current = `${PRODUCT_SLUG}${rest}`;
      const value = storage.getItem(key);
      if (value !== null && storage.getItem(current) === null) {
        storage.setItem(current, value);
        moved.push(current);
      }
      storage.removeItem(key);
    }
  } catch {
    // Storage unavailable: nothing to move.
  }
  return moved;
}

/**
 * The header that marks a Claude Code hook as installed by this product (in the hook's own
 * `headers`, so the user's settings carry no foreign keys): `X-<Name>-Hook`.
 */
export const HOOK_MARKER_HEADER = `X-${PRODUCT_NAME}-Hook`;

/** True for the hook marker header under the current name or any earlier one, in any case. */
export function isHookMarkerHeader(name: string): boolean {
  const lower = name.toLowerCase();
  return SHIPPED_SLUGS.some((s) => lower === `x-${s}-hook`);
}

/** One hook as Claude Code settings hold it; only `headers` matters here. */
export interface MarkedHook {
  headers?: Readonly<Record<string, unknown>> | null;
  [key: string]: unknown;
}

/**
 * True when the product installed this hook, under the current name or an earlier one, so
 * turning control off (or reinstalling after a rename) removes or replaces the old entries too
 * and never leaves two. Hooks without the marker are the user's own and are never touched.
 */
export function isOwnHook(hook: MarkedHook | null | undefined): boolean {
  const headers = hook?.headers;
  if (!headers || typeof headers !== 'object') return false;
  return Object.keys(headers).some(isHookMarkerHeader);
}

/** The headers a hook installed now carries: the caller's own plus the current marker. */
export function markHookHeaders(headers: Readonly<Record<string, string>> = {}, value = '1'): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers)) if (!isHookMarkerHeader(k)) out[k] = v;
  out[HOOK_MARKER_HEADER] = value;
  return out;
}
